// src/components/programs/ProgramForm/helpers/payloadBuilder.js

import { calculateProgramTotals } from "./quotationCalculations";

/**
 * Fill empty language fields with the value from the other language
 * This ensures that if one language is empty, it gets populated with the other language's value
 * Only applies if the field was actually left empty by the user (not filled in either language)
 * 
 * Data structure: { title: { ar: '', en: '88' }, ... }
 */
const fillEmptyLanguageFields = (data) => {
    const multiLangFields = ['title', 'description'];
    const filledData = { ...data };

    multiLangFields.forEach(fieldName => {
        // Get the current field object
        const fieldObj = filledData[fieldName];

        if (!fieldObj || typeof fieldObj !== 'object') {
            return;
        }

        const arValue = fieldObj.ar?.trim() || '';
        const enValue = fieldObj.en?.trim() || '';

        // Fill empty language with the other language's value
        const filledFieldObj = {
            ar: arValue,
            en: enValue,
        };

        // If Arabic is filled but English is empty, use Arabic value for English
        if (arValue && !enValue) {
            filledFieldObj.en = arValue;
        }

        // If English is filled but Arabic is empty, use English value for Arabic
        if (enValue && !arValue) {
            filledFieldObj.ar = enValue;
        }

        // Update the field with the filled values
        filledData[fieldName] = filledFieldObj;
    });

    return filledData;
};

/**
 * Build API payload from form data
 * This transforms the internal form structure to match the API requirements
 */
export const buildPayload = (formData) => {
    // Fill empty language fields first
    const processedFormData = fillEmptyLanguageFields(formData);

    // Build days payload
    const days = processedFormData.itinerary.map((day, index) => {
        // Extract products for this day
        const dayProducts = day.products.map(product => {
            // Base product data
            const productPayload = {
                product_id: product.product_snapshot.product_id,
                supplier_id: product.selected_supplier?.id || null,
                order: product.order || 1,
            };

            // ✅ Always send custom_price - if not edited, send the null
            productPayload.custom_price = product.pricing.custom_price !== null && product.pricing.custom_price !== undefined
                ? parseFloat(product.pricing.custom_price)
                : null;

            // ✅ إضافة’ original_sell_price للـ payload
            productPayload.original_sell_price = parseFloat(
                product.pricing.original_sell_price ||
                product.product_snapshot.original_sell_price ||
                product.product_snapshot.original_price ||
                0
            );

            // ✅ per_person: إرسال pax_type_prices المخصصة لكل pax_type على حدة
            const pricingUnit = product.product_snapshot?.pricing_unit;
            const customPaxPrices = product.pricing?.custom_pax_prices;
            const isPriceEdited = product.pricing?.is_price_edited;
            const snapshotPaxTypePrices = product.product_snapshot?.pax_type_prices || [];

            if (
                pricingUnit === 'per_person' &&
                snapshotPaxTypePrices.length > 0
            ) {
                const customPaxCosts = product.pricing?.custom_pax_costs;
                const hasAnyCustomSell = customPaxPrices && Object.keys(customPaxPrices).length > 0 && isPriceEdited;
                const hasAnyCustomCost = customPaxCosts && Object.keys(customPaxCosts).length > 0;

                // ✅ إرسال pax_type_prices إذا كان فيه أي تعديل على البيع أو التكلفة
                if (hasAnyCustomSell || hasAnyCustomCost) {
                    productPayload.pax_type_prices = snapshotPaxTypePrices.map(ptp => {
                        const paxTypeId = parseInt(ptp.pax_type_id);
                        const customPrice = hasAnyCustomSell ? customPaxPrices[paxTypeId] : undefined;
                        const customCost = customPaxCosts?.[paxTypeId];

                        const hasCustomPrice = customPrice !== undefined && customPrice !== null && !isNaN(parseFloat(customPrice));
                        const hasCustomCost = customCost !== undefined && customCost !== null && !isNaN(parseFloat(customCost));

                        return {
                            pax_type_id: paxTypeId,
                            price: hasCustomPrice ? parseFloat(customPrice) : parseFloat(ptp.price) || 0,
                            // ✅ NEW: إرسال cost_price فقط لو المستخدم عدّله فعلاً
                            ...(hasCustomCost && { cost_price: parseFloat(customCost) }),
                        };
                    });
                    // في per_person مع pax_type_prices نرسل custom_price كـ null
                    productPayload.custom_price = null;
                }
            }

            // ✅ FIX: Send custom_cost_price for non-per-person products (per_night, per_unit)
            if (
                pricingUnit !== 'per_person' &&
                isPriceEdited &&
                product.pricing?.custom_cost_price != null
            ) {
                productPayload.custom_cost_price = parseFloat(product.pricing.custom_cost_price);
            }
            // Add discount if exists
            if (product.pricing.discount_type && product.pricing.discount_type !== 'zero') {
                productPayload.discount_type = product.pricing.discount_type;
                productPayload.discount = parseFloat(product.pricing.discount_value) || 0;
            }

            // Extract dynamic fields and add them directly to product payload
            if (product.dynamic_fields) {
                if (product.dynamic_fields.time) {
                    productPayload.time = product.dynamic_fields.time;
                }

                if (product.dynamic_fields.pickup_time) {
                    productPayload.pickup_time = product.dynamic_fields.pickup_time;
                }

                if (product.dynamic_fields.city_id) {
                    productPayload.city_id = parseInt(product.dynamic_fields.city_id);
                }

                if (product.dynamic_fields.units !== null && product.dynamic_fields.units !== undefined) {
                    productPayload.units = parseInt(product.dynamic_fields.units);
                }

                if (product.dynamic_fields.nights !== null && product.dynamic_fields.nights !== undefined) {
                    productPayload.nights = parseInt(product.dynamic_fields.nights);
                }
            }

            return productPayload;
        });

        // Return day structure
        return {
            day_name: day.day_name,
            date: day.date,
            products: dayProducts,
        };
    }).filter(day => day.products.length > 0);

    // Calculate total number of pax
    const totalPax = processedFormData.pax_types.reduce((sum, pt) => sum + (parseInt(pt.count) || 0), 0);

    // Calculate program totals (including final sell price)
    const programTotals = calculateProgramTotals(processedFormData);

    // Build final payload
    const payload = {
        // Basic info
        title: processedFormData.title,
        description: processedFormData.description,
        agent_id: processedFormData.agent_id,
        // ✅ تحويل city_ids إلى أرقام لضمان توافق الـ API
        city_ids: (processedFormData.city_ids || []).map(id => parseInt(id)).filter(id => !isNaN(id)),

        // ✅ إضافة حقل cities كخيار احتياطي (Fallback) لتجنب أخطاء قاعدة البيانات
        cities: "",

        start_date: processedFormData.start_date,
        end_date: processedFormData.end_date,
        durations: parseInt(processedFormData.durations_days) || 0,
        nights: parseInt(processedFormData.durations_nights) || 0,
        show_date_in_template: !!processedFormData.show_date_in_template,

        // Number of pax
        number_of_pax: totalPax,

        // Program final price (after all discounts and adjustments)
        price: parseFloat(programTotals.final_selling_price.toFixed(2)),

        // Pax types
        pax_types: processedFormData.pax_types
            .filter(pt => pt.pax_type_id && parseInt(pt.count) > 0)
            .map(pt => ({
                pax_type_id: parseInt(pt.pax_type_id),
                quantity: parseInt(pt.count),
            })),

        // Days with products
        days: days,
    };

    // Add cover image if exists
    if (processedFormData.cover_image) {
        payload.cover_image = processedFormData.cover_image;
    }

    // ✅ Add global discount with target (استبدال category بـ target)
    if (processedFormData.global_discount.type && processedFormData.global_discount.type !== 'zero') {
        payload.discount_type = processedFormData.global_discount.type;
        payload.discount = parseFloat(processedFormData.global_discount.value) || 0;
        payload.discount_target = processedFormData.global_discount.target || 'sell';  // ✅ استخدام target
    } else {
        // Send zero discount explicitly
        payload.discount_type = 'zero';
        payload.discount = 0;
        payload.discount_target = 'sell';  // ✅ default value
    }

    return payload;
};
